import TagManager from 'react-gtm-module';
import React, { useEffect, useMemo, useState } from 'react';
import styles from './search-bar.module.scss';
import Typeahead from '../../atoms/typeahead/typeahead';
import { ApplicationSettings } from '../../../models/settings';
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { GetSearchSuggestions } from '../../../api-clients/search-api-client';
import { CrossIcon, SearchIcon } from '../../atoms/icons/icons';
import colorStyle from '../../../styles/utils/variables.module.scss';

type Props = {
  onSearch?: (keyword: string) => void;
  onAutoSuggestSelected?: (keyword: string) => void;
  initialKeyword: string;
  idPrefix: string;
  acceptEmptyValue?: boolean;
  customClassName?: string;
  cssWrapper?: string;
  typeAheadPlaceHolder: string;
  router: AppRouterInstance;
  translate: any;
  applicationSettings: ApplicationSettings;
  typeAheadActive?: boolean;
  currentLocale: string;
  themeColor?: string;
  themeCopyColor?: string;
  invertedTheme?: boolean;
  brandPrimaryColor?: string;
  getSearchSuggestions?: GetSearchSuggestions;
};

export default function SearchBar({
  onSearch,
  onAutoSuggestSelected,
  initialKeyword,
  idPrefix,
  acceptEmptyValue = false,
  customClassName = null,
  cssWrapper,
  typeAheadPlaceHolder,
  router,
  translate,
  applicationSettings,
  typeAheadActive = true,
  currentLocale,
  themeColor,
  themeCopyColor,
  invertedTheme,
  brandPrimaryColor,
  getSearchSuggestions,
}: Props) {
  const [keyword, setKeywordValue] = useState(initialKeyword);
  const [lastKeyword, setLastKeyword] = useState(initialKeyword);
  const [hideTypeaheadTrigger, setHideTypeaheadTrigger] = useState<boolean>(false);
  const [cssClass, setCssClassValue] = useState(cssWrapper);
  const [typeaheadInputPlaceholder, setTypeAheadPlaceHolder] = useState(cssWrapper);
  const [resetTypeahead, setResetTypeahead] = useState<boolean>(false);

  useEffect(() => {
    setKeywordValue(initialKeyword);
    setCssClassValue(cssWrapper);
    setTypeAheadPlaceHolder(typeAheadPlaceHolder);
  }, [cssWrapper, initialKeyword, typeAheadPlaceHolder]);

  // TODO: Refactoring required. Duplicate with the typeahead.tsx, selectFocusedResult
  function getValidSearchTerm(searchTerm: string) {
    // eslint-disable-next-line no-useless-escape
    return searchTerm.replace(/[^a-zA-Z0-9\-\.\, ]/g, ``).trim();
  }

  // TODO: Refactoring required. Duplicate with the typeahead.tsx, selectFocusedResult
  const onTypeaheadSearchHandler = (searchTerm: string) => {
    setKeywordValue(searchTerm);
  };

  // TODO: Refactoring required. Duplicate with the typeahead.tsx, selectFocusedResult
  function onSearchHandler(searchTerm: string) {
    if (onSearch) onSearch(searchTerm);
  }

  function pushDataLayer(keywordQuery: string) {
    if (process.env.NEXT_PUBLIC_GTM_CONTAINER_ID) {
      TagManager.dataLayer({
        dataLayer: { searchTerms: keywordQuery, event: `searchTerms` },
      });
    }
  }

  // TODO: Refactoring required. Duplicate with the typeahead.tsx, selectFocusedResult
  // push to XO Tracking via GTM dataLayer
  const handleSearchTerm = () => {
    const validSearchTerm = getValidSearchTerm(keyword);
    if (validSearchTerm.length && lastKeyword !== validSearchTerm) {
      pushDataLayer(validSearchTerm);
      onSearchHandler(validSearchTerm);
      setLastKeyword(validSearchTerm);
    }
    if (!validSearchTerm.length && acceptEmptyValue) {
      onSearchHandler(validSearchTerm);
      setLastKeyword(validSearchTerm);
    }
  };

  function startSearch(e) {
    if (e) {
      e.preventDefault();
    }
    handleSearchTerm();
  }

  const handleSearchButtonClick = () => {
    setHideTypeaheadTrigger(!hideTypeaheadTrigger);
  };

  const onTypeaheadAutoSuggestSelected = (searchTerm: string) => {
    setKeywordValue(searchTerm);
    handleSearchTerm();
    if (onAutoSuggestSelected) onAutoSuggestSelected(searchTerm);
  };

  const searchApiSettings = {
    baseUrl: `${process.env.NEXT_PUBLIC_SEARCH_SERVICE_URL}/v2`,
    searchTimeOut: 10000,
    applicationType: applicationSettings?.applicationType,
    b2cFlag: applicationSettings?.b2cFlag,
  };

  const searchButtonStyles: React.CSSProperties = useMemo(() => {
    return {
      '--theme-color': themeColor,
      '--brand-primary-color': brandPrimaryColor,
    } as React.CSSProperties;
  }, [themeColor, brandPrimaryColor]);

  const handleResetClick = () => {
    // Set the flag to trigger the reset in the child
    setResetTypeahead(true);
    // Reset the flag shortly after to avoid repeated resets
    setTimeout(() => {
      setResetTypeahead(false);
    }, 0);
  };

  return (
    <div
      className={`${styles[cssClass]} ${invertedTheme ? styles['inverted-theme'] : ''}`}
      style={searchButtonStyles}
    >
      <form
        className={`${styles.search} ${styles[customClassName]} ${styles[`${idPrefix}-form`]} search`}
        id={`${idPrefix}-bar`}
        onSubmit={startSearch}
        role='search'
      >
        {typeAheadActive ? (
          <Typeahead
            typeaheadInputId={idPrefix}
            typeaheadInputPlaceholder={typeaheadInputPlaceholder}
            typeaheadInputInitialValue={keyword}
            hideTypeaheadTrigger={hideTypeaheadTrigger}
            typeaheadInputValueChange={onTypeaheadSearchHandler}
            typeaheadAutoSuggestSelected={onTypeaheadAutoSuggestSelected}
            cssWrapper={cssClass}
            router={router}
            minimumTextLength={3}
            searchApiSettings={searchApiSettings}
            currentLocale={currentLocale}
            themeCopyColor={themeCopyColor}
            getSearchSuggestions={getSearchSuggestions}
            resetTypeahead={resetTypeahead}
            invertedTheme={invertedTheme}
          />
        ) : (
          <input
            type='search'
            className={styles[`search-term`]}
            placeholder="Search INTO's programs"
            autoComplete='off'
            onChange={(e) => {
              setKeywordValue(e.currentTarget.value);
            }}
            value={keyword}
            id={`${idPrefix}-input`}
          />
        )}
        <div className={styles[`buttons-wrapper`]}>
          {keyword.length > 0 && cssClass === `header` && (
            <button
              type='button'
              className={`${styles[`cross-button`]} image-wrapper`}
              onClick={typeAheadActive ? handleResetClick : () => setKeywordValue(``)}
              id={`${idPrefix}-cross-button`}
            >
              <CrossIcon fillColor={colorStyle.grey3} height={24} width={24} />
            </button>
          )}
          {(cssClass === 'header' || cssClass === 'microsite-ordered-search') && (
            <button
              type='submit'
              className={`${styles[`search-button`]}`}
              id={`${idPrefix}-button`}
              aria-label={`${idPrefix}-input`}
              onClick={handleSearchButtonClick}
            >
              <SearchIcon fillColor={colorStyle.white} height={16} width={16} />
            </button>
          )}
        </div>
        {cssClass === `homepage` && (
          <button
            className={`${styles[`hero-search-btn`]}`}
            type='submit'
            id='heroDegreeSearchSubmit'
            aria-label={translate(`homepage.find-programs`)}
          >
            {translate(`homepage.find-programs`)}
          </button>
        )}
      </form>
    </div>
  );
}

